import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Drawer,
  useTheme,
  useMediaQuery,
  Collapse,
  Fab,
  Avatar
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  Visibility,
  VisibilityOff,
  FilterList as FilterIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import useAuth from './useAuth';
import api from './api';

// Profile Card Component
const ProfileCard = ({ user, onChangePassword }) => {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 60,
                height: 60,
                mr: 2,
                fontSize: '1.5rem'
              }}
            >
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {user?.username}
              </Typography>
              <Chip
                label={user?.role?.replace('_', ' ').toUpperCase()}
                color="primary"
                size="small"
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Email Address
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body1">{user?.email}</Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Last Login
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body1">
              {user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<LockIcon />}
            onClick={onChangePassword}
            sx={{ mt: 2 }}
          >
            Change Password
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

// Password Change Form Component
const PasswordForm = ({ open, onClose, onSave, loading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswords({ current: false, new: false, confirm: false });
    setErrors({});
  }, [open]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.currentPassword) newErrors.currentPassword = 'Current password is required';
    if (!formData.newPassword) newErrors.newPassword = 'New password is required';
    if (formData.newPassword.length < 8) newErrors.newPassword = 'Password must be at least 8 characters long';
    if (formData.newPassword !== formData.confirmPassword) newErrors.confirmPassword = 'New passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    onSave(formData);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : 2 }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Change Password
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Password Information
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" error={!!errors.currentPassword}>
                <InputLabel>Current Password</InputLabel>
                <OutlinedInput
                  type={showPasswords.current ? 'text' : 'password'}
                  value={formData.currentPassword}
                  onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={() => togglePasswordVisibility('current')}>
                        {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Current Password"
                  size="small"
                />
                {errors.currentPassword && (
                  <Typography variant="caption" color="error">{errors.currentPassword}</Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" error={!!errors.newPassword}>
                <InputLabel>New Password</InputLabel>
                <OutlinedInput
                  type={showPasswords.new ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => handleInputChange('newPassword', e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={() => togglePasswordVisibility('new')}>
                        {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="New Password"
                  size="small"
                />
                {errors.newPassword && (
                  <Typography variant="caption" color="error">{errors.newPassword}</Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" error={!!errors.confirmPassword}>
                <InputLabel>Confirm New Password</InputLabel>
                <OutlinedInput
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={() => togglePasswordVisibility('confirm')}>
                        {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Confirm New Password"
                  size="small"
                />
                {errors.confirmPassword && (
                  <Typography variant="caption" color="error">{errors.confirmPassword}</Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Change Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Profile Component
const Profile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { user, token } = useAuth();
  const [formOpen, setFormOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const handleSavePassword = async (formData) => {
    setLoading(true);
    try {
      const response = await api.changePassword(token, formData.currentPassword, formData.newPassword);
      if (response.success) {
        setSuccess('Password changed successfully');
        setFormOpen(false);
      } else {
        setError(response.error || 'Failed to change password');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: 2,
        mb: 3
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Profile Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your account details and security settings
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={() => setFormOpen(true)}
            startIcon={<LockIcon />}
          >
            Change Password
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      <Collapse in={!!error}>
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Collapse>

      <Collapse in={!!success}>
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      </Collapse>

      {/* Mobile Filters Drawer (Placeholder for consistency, though not used in Profile) */}
      {isSmallMobile && (
        <Drawer
          anchor="bottom"
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          PaperProps={{
            sx: { borderRadius: '16px 16px 0 0' }
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6">Settings</Typography>
              <IconButton onClick={() => setFiltersOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary">
              No additional settings available
            </Typography>
          </Box>
        </Drawer>
      )}

      {/* Profile Content */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: 'primary.main' }} /> User Profile
          </Typography>
          {isSmallMobile && (
            <Button
              variant="outlined"
              onClick={() => setFiltersOpen(true)}
              startIcon={<FilterIcon />}
              size="small"
            >
              Settings
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ProfileCard
                user={user}
                onChangePassword={() => setFormOpen(true)}
              />
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Floating Action Button for Mobile */}
      {isSmallMobile && (
        <Fab
          color="primary"
          onClick={() => setFormOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24
          }}
        >
          <LockIcon />
        </Fab>
      )}

      {/* Password Form Dialog */}
      <PasswordForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleSavePassword}
        loading={loading}
      />
    </Container>
  );
};

export default Profile;